import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Plus, Trash2, Edit3, Camera, ArrowRight, Search, X, Brain, FileText, ChevronDown, ChevronUp, Lightbulb, ShieldAlert, Download, Zap, ExternalLink, Copy, CheckCheck } from 'lucide-react';

function QuickBudgetForm({ onSave, onCancel }) {
  const [form, setForm] = useState({ client_name: '', client_phone: '', location: '', description: '', status: 'rascunho' });
  const [aiLoading, setAiLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const runQuickAI = async () => {
    if (!form.description) return;
    setAiLoading(true);
    try {
      const { data } = await api.post('/ai/analyze-budget', { description: form.description, location: form.location });
      if (data.success && data.analysis) {
        setResult(data.analysis);
      }
    } catch {} finally { setAiLoading(false); }
  };

  const handleSave = async () => {
    const budget = {
      ...form,
      items: result?.items || [],
      total: result?.total || 0,
      estimated_days: result?.estimated_days || 0,
      estimated_workers: result?.estimated_workers || 0,
      ai_suggestions: result ? { sugestoes: result.sugestoes || [], cuidados: result.cuidados || [] } : null,
    };
    onSave(budget);
  };

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <div className="flex items-center justify-between mb-4">
        <div><h1 className="text-xl font-bold flex items-center gap-2"><Zap size={20} style={{ color: 'var(--warning)' }} /> Orcamento Rapido</h1><p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Crie um orcamento com AI em segundos</p></div>
        <button onClick={onCancel} className="btn btn-outline btn-sm"><X size={16} /></button>
      </div>
      <div className="pro-card-padded space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="field-label">Cliente</label><input data-testid="quick-client" className="input" placeholder="Nome do cliente" value={form.client_name} onChange={e => set('client_name', e.target.value)} /></div>
          <div><label className="field-label">Telefone</label><input data-testid="quick-phone" className="input" placeholder="+351..." value={form.client_phone} onChange={e => set('client_phone', e.target.value)} /></div>
        </div>
        <div><label className="field-label">Local da Obra</label><input data-testid="quick-location" className="input" placeholder="Ex: Rua da Liberdade, Lisboa" value={form.location} onChange={e => set('location', e.target.value)} /></div>
        <div>
          <label className="field-label">Descricao do Trabalho</label>
          <textarea data-testid="quick-description" className="input" rows={4} placeholder="Descreva o trabalho a realizar. Quanto mais detalhes, melhor a estimativa da AI..." value={form.description} onChange={e => set('description', e.target.value)} />
        </div>
        <button data-testid="quick-ai-btn" onClick={runQuickAI} disabled={aiLoading || !form.description} className="btn btn-primary w-full">
          {aiLoading ? <><span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" /> A analisar...</> : <><Brain size={16} /> Gerar Orcamento com AI</>}
        </button>

        {result && (
          <div className="mt-4 space-y-3" data-testid="quick-result">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 rounded" style={{ background: 'var(--surface-alt)' }}><p className="text-[10px]" style={{ color: 'var(--text-light)' }}>Total</p><p className="text-sm font-bold">{(result.total || 0).toLocaleString('pt-PT')} EUR</p></div>
              <div className="p-2 rounded" style={{ background: 'var(--surface-alt)' }}><p className="text-[10px]" style={{ color: 'var(--text-light)' }}>Dias</p><p className="text-sm font-bold">{result.estimated_days || 0}</p></div>
              <div className="p-2 rounded" style={{ background: 'var(--surface-alt)' }}><p className="text-[10px]" style={{ color: 'var(--text-light)' }}>Funcionarios</p><p className="text-sm font-bold">{result.estimated_workers || 0}</p></div>
            </div>
            {result.items?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="pro-table text-xs">
                  <thead><tr><th>Item</th><th>Qtd</th><th>Subtotal</th></tr></thead>
                  <tbody>{result.items.map((it, i) => (<tr key={i}><td>{it.descricao}</td><td>{it.quantidade} {it.unidade}</td><td className="font-semibold">{(it.subtotal || 0).toFixed(2)} EUR</td></tr>))}</tbody>
                </table>
              </div>
            )}
            <div className="flex gap-2">
              <button data-testid="quick-save-btn" onClick={handleSave} className="btn btn-primary flex-1">Guardar Orcamento</button>
              <button onClick={() => setResult(null)} className="btn btn-outline">Limpar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BudgetForm({ budget, onSave, onCancel }) {
  const [form, setForm] = useState(budget || {
    client_name: '', client_email: '', client_phone: '', location: '',
    description: '', estimated_days: 0, estimated_workers: 0,
    items: [], total: 0, status: 'rascunho', photos: [], notes: '',
    ai_suggestions: null
  });
  const [aiLoading, setAiLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [saveHistory, setSaveHistory] = useState(false);
  const [notifyClient, setNotifyClient] = useState(false);
  const [versionNotes, setVersionNotes] = useState('');

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const addItem = () => {
    set('items', [...(form.items || []), { descricao: '', unidade: 'un', quantidade: 1, preco_unitario: 0, subtotal: 0 }]);
  };

  const updateItem = (i, field, val) => {
    const items = [...form.items];
    items[i] = { ...items[i], [field]: val };
    if (field === 'quantidade' || field === 'preco_unitario') {
      items[i].subtotal = (parseFloat(items[i].quantidade) || 0) * (parseFloat(items[i].preco_unitario) || 0);
    }
    setForm(p => ({ ...p, items, total: items.reduce((s, it) => s + (it.subtotal || 0), 0) }));
  };

  const removeItem = (i) => {
    const items = form.items.filter((_, idx) => idx !== i);
    setForm(p => ({ ...p, items, total: items.reduce((s, it) => s + (it.subtotal || 0), 0) }));
  };

  const runAiAnalysis = async (photoBase64) => {
    if (!form.description && !photoBase64) return;
    setAiLoading(true);
    try {
      const payload = { description: form.description, location: form.location };
      if (photoBase64) payload.image_base64 = photoBase64;
      const { data } = await api.post('/ai/analyze-budget', payload);
      if (data.success && data.analysis) {
        const a = data.analysis;
        if (a.items?.length) setForm(p => ({ ...p, items: a.items, total: a.total || a.items.reduce((s, it) => s + (it.subtotal || 0), 0) }));
        if (a.estimated_workers) set('estimated_workers', a.estimated_workers);
        if (a.estimated_days) set('estimated_days', a.estimated_days);
        if (a.sugestoes || a.cuidados || a.maquinas || a.alertas || a.estado_obra) {
          set('ai_suggestions', { sugestoes: a.sugestoes || [], cuidados: a.cuidados || [], maquinas: a.maquinas || [], alertas: a.alertas || [], estado_obra: a.estado_obra || '', notas: a.notas || '' });
          setShowSuggestions(true);
        }
      }
    } catch {}
    setAiLoading(false);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result.split(',')[1];
      set('photos', [...(form.photos || []), { name: file.name, base64 }]);
      runAiAnalysis(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    const payload = { ...form };
    if (budget && saveHistory) {
      payload.save_history = true;
      payload.version_notes = versionNotes;
    }
    if (budget && notifyClient) {
      payload.notify_client = true;
    }
    onSave(payload);
  };

  return (
    <div className="animate-in">
      <div className="flex items-center justify-between mb-4 pt-10 lg:pt-0">
        <div>
          <h1 className="text-xl font-bold">{budget ? 'Editar Orcamento' : 'Novo Orcamento'}</h1>
          {budget && <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Versao {budget.version || 1}</p>}
        </div>
        <button data-testid="close-budget-form" onClick={onCancel} className="btn btn-outline btn-sm"><X size={16} /> Fechar</button>
      </div>

      <div className="pro-card-padded mb-4">
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Dados do Cliente</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><label className="field-label">Cliente</label><input data-testid="budget-client-name" className="input" placeholder="Nome" value={form.client_name} onChange={e => set('client_name', e.target.value)} /></div>
          <div><label className="field-label">Email</label><input data-testid="budget-client-email" className="input" placeholder="email@cliente.pt" value={form.client_email} onChange={e => set('client_email', e.target.value)} /></div>
          <div><label className="field-label">Telefone</label><input data-testid="budget-client-phone" className="input" placeholder="+351..." value={form.client_phone} onChange={e => set('client_phone', e.target.value)} /></div>
        </div>
      </div>

      <div className="pro-card-padded mb-4">
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Detalhes do Trabalho</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div><label className="field-label">Local</label><input data-testid="budget-location" className="input" value={form.location} onChange={e => set('location', e.target.value)} /></div>
          <div><label className="field-label">Estado</label>
            <select data-testid="budget-status" className="select" value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="rascunho">Rascunho</option><option value="enviado">Enviado</option><option value="aprovado">Aprovado</option><option value="rejeitado">Rejeitado</option>
            </select>
          </div>
        </div>
        <div className="mb-3">
          <label className="field-label">Descricao do Trabalho</label>
          <textarea data-testid="budget-description" className="input" rows={3} placeholder="Descreva detalhadamente o trabalho a realizar..." value={form.description} onChange={e => set('description', e.target.value)} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className="field-label">Dias Estimados</label><input data-testid="budget-days" className="input" type="number" value={form.estimated_days} onChange={e => set('estimated_days', parseInt(e.target.value) || 0)} /></div>
          <div>
            <label className="field-label">Funcionarios (sugerido por AI)</label>
            <input data-testid="budget-workers" className="input" type="number" value={form.estimated_workers} onChange={e => set('estimated_workers', parseInt(e.target.value) || 0)} />
          </div>
        </div>
      </div>

      {/* AI Analysis */}
      <div className="pro-card-padded mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
            <Brain size={16} /> Analise AI
          </h3>
          <button data-testid="run-ai-btn" onClick={() => runAiAnalysis(null)} className="btn btn-primary btn-sm" disabled={aiLoading || !form.description}>
            <Brain size={14} /> {aiLoading ? 'A analisar...' : 'Analisar com AI'}
          </button>
        </div>
        <p className="text-xs mb-3" style={{ color: 'var(--text-light)' }}>
          A AI analisa a descricao (e foto opcional) para sugerir orcamento, numero de funcionarios, procedimentos e cuidados de seguranca.
        </p>
        <label className="upload-zone block cursor-pointer">
          <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} data-testid="budget-photo-upload" />
          <Camera size={24} className="mx-auto mb-1" style={{ color: 'var(--text-light)' }} />
          <p className="text-xs font-medium">{aiLoading ? 'A analisar...' : 'Adicionar foto do local (opcional)'}</p>
        </label>
        {form.photos?.length > 0 && (
          <div className="flex gap-2 mt-2 flex-wrap">{form.photos.map((p, i) => <span key={i} className="badge badge-info">{p.name}</span>)}</div>
        )}
      </div>

      {/* AI Suggestions */}
      {form.ai_suggestions && (
        <div className="pro-card-padded mb-4" style={{ borderLeft: '3px solid var(--accent)' }}>
          <button onClick={() => setShowSuggestions(!showSuggestions)} className="flex items-center justify-between w-full text-left" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--accent)' }}>
              <Lightbulb size={16} /> Sugestoes e Cuidados AI
            </h3>
            {showSuggestions ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {showSuggestions && (
            <div className="mt-3 space-y-3">
              {form.ai_suggestions.estado_obra && (
                <div className="p-2.5 rounded-md" style={{ background: 'var(--surface-alt)', border: '1px solid var(--border-light)' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: 'var(--primary)' }}>Estado da Obra</p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{form.ai_suggestions.estado_obra}</p>
                </div>
              )}
              {form.ai_suggestions.alertas?.length > 0 && (
                <div className="p-2.5 rounded-md" style={{ background: '#FDECEB', border: '1px solid #F5C6CB' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: 'var(--error)' }}><ShieldAlert size={12} className="inline mr-1" />Alertas - O que pode estar a escapar</p>
                  <ul className="text-xs space-y-1 ml-4">{form.ai_suggestions.alertas.map((a, i) => <li key={i} className="list-disc" style={{ color: '#9B2C2C' }}>{a}</li>)}</ul>
                </div>
              )}
              {form.ai_suggestions.maquinas?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold mb-1" style={{ color: 'var(--primary)' }}>Maquinas e Equipamentos</p>
                  <div className="overflow-x-auto">
                    <table className="pro-table text-xs">
                      <thead><tr><th>Tipo</th><th>Capacidade</th><th>Horas</th><th>Custo Est.</th></tr></thead>
                      <tbody>{form.ai_suggestions.maquinas.map((m, i) => <tr key={i}><td>{m.tipo}</td><td>{m.capacidade}</td><td>{m.horas}h</td><td>{(m.custo_estimado || 0).toFixed(2)} EUR</td></tr>)}</tbody>
                    </table>
                  </div>
                </div>
              )}
              {form.ai_suggestions.sugestoes?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold mb-1 flex items-center gap-1" style={{ color: 'var(--info)' }}><Lightbulb size={12} /> Sugestoes Tecnicas</p>
                  <ul className="text-xs space-y-1 ml-4">{form.ai_suggestions.sugestoes.map((s, i) => <li key={i} className="list-disc" style={{ color: 'var(--text-secondary)' }}>{s}</li>)}</ul>
                </div>
              )}
              {form.ai_suggestions.cuidados?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold mb-1 flex items-center gap-1" style={{ color: 'var(--warning)' }}><ShieldAlert size={12} /> Cuidados e Seguranca</p>
                  <ul className="text-xs space-y-1 ml-4">{form.ai_suggestions.cuidados.map((c, i) => <li key={i} className="list-disc" style={{ color: 'var(--text-secondary)' }}>{c}</li>)}</ul>
                </div>
              )}
              {form.ai_suggestions.notas && <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{form.ai_suggestions.notas}</p>}
            </div>
          )}
        </div>
      )}

      {/* Items */}
      <div className="pro-card-padded mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Itens do Orcamento</h3>
          <button data-testid="add-budget-item" onClick={addItem} className="btn btn-outline btn-sm"><Plus size={14} /> Item</button>
        </div>
        {form.items?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="pro-table">
              <thead><tr><th>Descricao</th><th>Un.</th><th>Qtd.</th><th>Preco Un.</th><th>Subtotal</th><th></th></tr></thead>
              <tbody>
                {form.items.map((item, i) => (
                  <tr key={i}>
                    <td><input className="input" style={{minWidth:'150px'}} value={item.descricao} onChange={e => updateItem(i, 'descricao', e.target.value)} /></td>
                    <td><input className="input" style={{width:'60px'}} value={item.unidade} onChange={e => updateItem(i, 'unidade', e.target.value)} /></td>
                    <td><input className="input" style={{width:'70px'}} type="number" value={item.quantidade} onChange={e => updateItem(i, 'quantidade', parseFloat(e.target.value) || 0)} /></td>
                    <td><input className="input" style={{width:'90px'}} type="number" value={item.preco_unitario} onChange={e => updateItem(i, 'preco_unitario', parseFloat(e.target.value) || 0)} /></td>
                    <td className="font-semibold whitespace-nowrap">{(item.subtotal || 0).toFixed(2)} EUR</td>
                    <td><button onClick={() => removeItem(i)} className="btn btn-icon" style={{color:'var(--error)', background:'none', border:'none'}}><Trash2 size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-xs" style={{ color: 'var(--text-light)' }}>Sem itens. Use a AI ou adicione manualmente.</p>}
        <div className="flex justify-end mt-3">
          <div className="px-4 py-2 rounded-md" style={{ background: '#E8EDF4' }}>
            <span className="text-sm font-semibold" style={{ color: 'var(--primary)' }}>Total: {(form.total || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })} EUR</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="pro-card-padded mb-4">
        <label className="field-label">Notas Internas</label>
        <textarea className="input" rows={2} value={form.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Notas internas (nao visiveis ao cliente)..." />
      </div>

      {/* Version control for edits */}
      {budget && (
        <div className="pro-card-padded mb-4" style={{ background: 'var(--surface-alt)' }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Opcoes de Atualizacao</h3>
          <label className="flex items-center gap-2 text-xs font-medium cursor-pointer mb-2">
            <input type="checkbox" checked={saveHistory} onChange={e => setSaveHistory(e.target.checked)} className="w-4 h-4 rounded" />
            Guardar versao anterior no historico
          </label>
          {saveHistory && (
            <input className="input mb-2" placeholder="Notas desta versao..." value={versionNotes} onChange={e => setVersionNotes(e.target.value)} />
          )}
          {budget.is_project && (
            <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
              <input type="checkbox" checked={notifyClient} onChange={e => setNotifyClient(e.target.checked)} className="w-4 h-4 rounded" />
              Informar cliente da atualizacao
            </label>
          )}
        </div>
      )}

      <div className="flex gap-2 justify-end">
        <button data-testid="cancel-budget-btn" onClick={onCancel} className="btn btn-outline">Cancelar</button>
        <button data-testid="save-budget-btn" onClick={handleSave} className="btn btn-primary">Guardar Orcamento</button>
      </div>
    </div>
  );
}

const STATUS_BADGE = { rascunho: 'badge-neutral', enviado: 'badge-info', aprovado: 'badge-success', rejeitado: 'badge-error' };
const STATUS_LABEL = { rascunho: 'Rascunho', enviado: 'Enviado', aprovado: 'Aprovado', rejeitado: 'Rejeitado' };

export default function BudgetsPage() {
  const [budgets, setBudgets] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [quickMode, setQuickMode] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const load = useCallback(() => { api.get('/budgets').then(r => setBudgets(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    try {
      if (editing) await api.put(`/budgets/${editing.id}`, form);
      else await api.post('/budgets', form);
      setEditing(null); setCreating(false); setQuickMode(false); load();
    } catch {}
  };

  const handleDelete = async (id) => { if (window.confirm('Eliminar?')) { await api.delete(`/budgets/${id}`); load(); } };
  const handleToProject = async (id) => { if (window.confirm('Converter em projeto?')) { await api.post(`/budgets/${id}/to-project`); load(); } };
  const handleDownloadPdf = async (id, clientName) => {
    try {
      const response = await api.get(`/budgets/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `orcamento_${(clientName || 'sem_nome').replace(/\s+/g, '_').toLowerCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Erro ao gerar PDF'); }
  };

  const filtered = budgets.filter(b => {
    if (filterStatus && b.status !== filterStatus) return false;
    if (search && ![b.client_name, b.location, b.description].some(f => f?.toLowerCase().includes(search.toLowerCase()))) return false;
    return true;
  });

  if (quickMode) return <QuickBudgetForm onSave={handleSave} onCancel={() => setQuickMode(false)} />;
  if (creating || editing) return <BudgetForm budget={editing} onSave={handleSave} onCancel={() => { setEditing(null); setCreating(false); }} />;

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold">Orcamentos</h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{budgets.length} registo{budgets.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="quick-budget-btn" onClick={() => setQuickMode(true)} className="btn btn-outline btn-sm"><Zap size={14} /> Rapido</button>
          <button data-testid="new-budget-btn" onClick={() => setCreating(true)} className="btn btn-primary"><Plus size={16} /> Novo Orcamento</button>
        </div>
      </div>
      <div className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-light)' }} />
          <input data-testid="budget-search" className="input pl-9" placeholder="Pesquisar..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select data-testid="budget-filter-status" className="select" style={{ width: 'auto' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Todos os estados</option>
          <option value="rascunho">Rascunho</option>
          <option value="enviado">Enviado</option>
          <option value="aprovado">Aprovado</option>
          <option value="rejeitado">Rejeitado</option>
        </select>
      </div>
      {filtered.length === 0 ? (
        <div className="pro-card-padded text-center py-12">
          <FileText size={40} className="mx-auto mb-3" style={{ color: 'var(--text-light)' }} />
          <p className="font-semibold">Sem orcamentos</p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Crie o primeiro orcamento</p>
        </div>
      ) : (
        <div className="pro-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="pro-table" data-testid="budgets-table">
              <thead><tr><th>Cliente</th><th>Local</th><th>Total</th><th>Estado</th><th>Portal</th><th>Acoes</th></tr></thead>
              <tbody>
                {filtered.map(b => (
                  <tr key={b.id} data-testid={`budget-row-${b.id}`}>
                    <td><span className="font-medium">{b.client_name || '-'}</span><br/><span className="text-[10px]" style={{color:'var(--text-light)'}}>{b.description?.slice(0,40) || ''}</span></td>
                    <td className="text-xs">{b.location || '-'}</td>
                    <td className="font-semibold whitespace-nowrap">{(b.total || 0).toLocaleString('pt-PT')} EUR</td>
                    <td><span className={`badge ${STATUS_BADGE[b.status] || 'badge-neutral'}`}>{STATUS_LABEL[b.status] || b.status}</span></td>
                    <td>{b.client_portal_link ? (
                      <div className="flex gap-1 items-center">
                        <button
                          data-testid={`portal-link-${b.id}`}
                          onClick={() => window.open(`${window.location.origin}/orcamento/${b.client_portal_link}`, '_blank')}
                          className="btn btn-outline btn-xs"
                          title="Abrir portal do cliente"
                        >
                          <ExternalLink size={11} /> Abrir
                        </button>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/orcamento/${b.client_portal_link}`);
                          }}
                          className="btn btn-outline btn-xs"
                          title="Copiar link"
                        >
                          <Copy size={11} />
                        </button>
                      </div>
                    ) : <span className="text-[10px]" style={{color:'var(--text-light)'}}>-</span>}</td>
                    <td>
                      <div className="flex gap-1">
                        <button data-testid={`edit-budget-${b.id}`} onClick={() => setEditing(b)} className="btn btn-outline btn-xs"><Edit3 size={12} /></button>
                        <button data-testid={`pdf-budget-${b.id}`} onClick={() => handleDownloadPdf(b.id, b.client_name)} className="btn btn-outline btn-xs" title="Exportar PDF"><Download size={12} /></button>
                        {!b.is_project && b.status === 'aprovado' && (
                          <button data-testid={`convert-budget-${b.id}`} onClick={() => handleToProject(b.id)} className="btn btn-success btn-xs" title="Converter em Obra"><ArrowRight size={12} /></button>
                        )}
                        <button data-testid={`delete-budget-${b.id}`} onClick={() => handleDelete(b.id)} className="btn btn-danger btn-xs"><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
